// createCoalescedRunner — collapse a burst of triggers into a single async run.
//
// SupabaseStore subscribes to ~15 tables and (today) called fetchAll directly
// on every postgres_changes event. One logical action writes to several tables,
// so a single edit fired ~6 full refetches *per connected client*, and with no
// in-flight guard those refetches overlapped and applied out of order (an older
// snapshot could land last and clobber newer state). Under many clients that's
// a refetch storm that exhausts the DB connection pool.
//
// This wraps the refetch in three guarantees:
//   - Debounce: a burst of schedule() calls within `debounceMs` runs once.
//   - Single-flight: at most one run is ever in flight, so results can't apply
//     out of order — there's never more than one resolving at a time.
//   - Trailing run: if any trigger arrived while a run was executing, exactly
//     one more run happens afterward, so no change is missed.

export interface CoalescedRunner {
  /** Request a run. Debounced + coalesced; safe to call in a tight burst. */
  schedule: () => void;
  /** Run as soon as possible, bypassing the debounce (e.g. the initial load). */
  runNow: () => Promise<void>;
  /** Cancel any pending (not-yet-started) run. */
  dispose: () => void;
}

export function createCoalescedRunner(
  run: () => Promise<void>,
  opts: { debounceMs: number } = { debounceMs: 250 },
): CoalescedRunner {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let running = false;
  let pending = false; // a trigger arrived while a run was in flight

  const clearTimer = () => {
    if (timer != null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const fire = async () => {
    running = true;
    try {
      await run();
    } catch (err) {
      // run() is expected to handle its own errors; this only guards the
      // runner's bookkeeping against an unexpected throw.
      // eslint-disable-next-line no-console
      console.error('[coalesce] run failed:', err);
    } finally {
      running = false;
      if (pending) {
        pending = false;
        // Re-debounce: a steady stream of triggers keeps coalescing rather
        // than running back-to-back.
        schedule();
      }
    }
  };

  function schedule() {
    if (running) {
      pending = true;
      return;
    }
    clearTimer();
    timer = setTimeout(() => {
      timer = null;
      void fire();
    }, opts.debounceMs);
  }

  const runNow = async () => {
    clearTimer();
    if (running) {
      // A run is already in flight; make sure a trailing run follows so this
      // request isn't lost, then let the caller proceed.
      pending = true;
      return;
    }
    await fire();
  };

  const dispose = () => {
    clearTimer();
  };

  return { schedule, runNow, dispose };
}
